import React, {useContext, useMemo, useState} from 'react';
import parse from 'html-react-parser';
import styles from './CharacteristicsA.scss';
import blockStyles from '@baseStyles/block.scss';
import {ContextServicesFeaturesTable} from '@contexts/ContextServicesFeaturesTable';
import Price from '@components/base/Price/Price';
import Tooltip from '@components/base/Tooltip/Tooltip';
import useDockedTable from '@hooks/useDockedTable';
import language from '@lang/language';

const renewTermsConfig = {
	default: [
		{term: 1},
		{term: 12}
	],
	oneYear: [
		{term: 12}
	],
	WPSupport: [
		{term: 12}
	],
	ManagedVPS: [
		{term: 1},
		{term: 3},
		{term: 6},
		{term: 12}
	]
};

const oneYearServices = [
	'wp_support_02',
	'wp_support_03'
];

////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////

const CharacteristicsA = ({data = {}, color = ''}) => {
	const [openedToolTip, setOpenedToolTip] = useState(null);

	const serviceFeaturesContext = useContext(ContextServicesFeaturesTable);
	const contextData = serviceFeaturesContext.contextData || {};

	const {
		characteristicsData = {},
		groups = [],
		availablePlans = []
	} = contextData;

	useDockedTable({
		tableSelector: `.${styles.characteristicsTable}`
	});

	const characteristicsServiceType = groups?.[0]?.group_service_type || '';
	const firstPlanValue = availablePlans?.[0]?.value;

	const renewTerms = useMemo(() => {
		return (
		  renewTermsConfig[characteristicsServiceType] ||
		  (oneYearServices.includes(firstPlanValue) ? renewTermsConfig.oneYear : null) ||
		  renewTermsConfig.default
		);
	}, [characteristicsServiceType, firstPlanValue]);

	return (
	  <div className={`${styles.container} ${color ? blockStyles[color] : ''}`}>
		  {groups.map((group) => (
			<GroupTable
			  key={group.group_id}
			  group={group}
			  data={data}
			  availablePlans={availablePlans}
			  characteristicsData={characteristicsData}
			  openedToolTip={openedToolTip}
			  setOpenedToolTip={setOpenedToolTip}
			  characteristicsServiceType={characteristicsServiceType}
			/>
		  ))}

		  <RenewTable
			data={data}
			availablePlans={availablePlans}
			renewTerms={renewTerms}
			characteristicsServiceType={characteristicsServiceType}
		  />
	  </div>
	);
};

// Helper components
////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////

const GroupTable = ({
						group,
						data,
						availablePlans,
						characteristicsData,
						openedToolTip,
						setOpenedToolTip,
						characteristicsServiceType
					}) => {
	const rows = getGroupRows(characteristicsData, group.group_id, availablePlans);

	if (!rows.length) {
		return null;
	}

	return (
	  <div className={styles.tableWrap}>
		  <table
			className={`${styles.characteristicsTable} ${styles[characteristicsServiceType] ? styles[characteristicsServiceType] : ''}`}>
			  <TableHeader
				title={group.group_name_multilang}
				availablePlans={availablePlans}
				data={data}
			  />

			  <tbody>
			  {rows.map((param) => (
				<GroupRow
				  key={param.char_id}
				  groupId={group.group_id}
				  param={param}
				  availablePlans={availablePlans}
				  characteristicsData={characteristicsData}
				  openedToolTip={openedToolTip}
				  setOpenedToolTip={setOpenedToolTip}
				/>
			  ))}
			  </tbody>
		  </table>
	  </div>
	);
};

// Table header
const TableHeader = ({title, availablePlans, data}) => {
	return (
	  <thead>
	  <tr>
		  <th>
			  <h3 className={blockStyles.h4}>{title}</h3>
		  </th>

		  {availablePlans.map((option) => (
			<th key={option.value}>
				{getPlanName(data, option)}
			</th>
		  ))}
	  </tr>
	  </thead>
	);
};

// Table group row
const GroupRow = ({
					  groupId,
					  param,
					  availablePlans,
					  characteristicsData,
					  openedToolTip,
					  setOpenedToolTip
				  }) => {
	const isMutual = isMutualCharacteristic(characteristicsData, groupId, param, availablePlans);

	// If characteristic value for all plans are same, 
	// display one column with mutual value and colspan for all plans, 
	// instead of separate column for each plan with same value
	if (isMutual) {
		const mutualColumnData = getCharacteristicForPlan(
		  characteristicsData,
		  groupId,
		  availablePlans?.[0]?.value,
		  param.char_id
		) || param;

		return (
		  <tr>
			  <RowTitleColumn
				param={param}
				openedToolTip={openedToolTip}
				setOpenedToolTip={setOpenedToolTip}
			  />

			  <ValueColumn
				columnKey={`${param.char_id}-mutual`}
				columnData={mutualColumnData}
				colSpan={availablePlans.length}
				openedToolTip={openedToolTip}
				setOpenedToolTip={setOpenedToolTip}
			  />
		  </tr>
		);
	}

	// Return with separate columns
	return (
	  <tr>
		  <RowTitleColumn
			param={param}
			openedToolTip={openedToolTip}
			setOpenedToolTip={setOpenedToolTip}
		  />

		  {availablePlans.map((option) => {
			  const columnData = getCharacteristicForPlan(
				characteristicsData,
				groupId,
				option.value,
				param.char_id
			  );

			  return (
				<ValueColumn
				  key={`${param.char_id}-${option.value}`}
				  columnKey={`${param.char_id}-${option.value}`}
				  columnData={columnData}
				  openedToolTip={openedToolTip}
				  setOpenedToolTip={setOpenedToolTip}
				/>
			  );
		  })}
	  </tr>
	);
};

// Characteristic name
const RowTitleColumn = ({
							param,
							openedToolTip,
							setOpenedToolTip
						}) => {
	return (
	  <th scope="row">
		  {param.char_tooltip ? (
			  /* If is tooltip, open tooltip onclick over characteristic name */
			  <Tooltip
				id={`tp${param.char_id}`}
				text={param.char_name}
				tooltipText={param.char_tooltip}
				openedId={openedToolTip}
				setOpenedId={setOpenedToolTip}
			  />
			)
			:
			(
			  /* Normal characteristic name */
			  <span
				dangerouslySetInnerHTML={{
					__html: param.char_name
				}}
			  />
			)}
	  </th>
	);
};

// Value table column
const ValueColumn = ({
						 columnData,
						 columnKey,
						 colSpan,
						 openedToolTip,
						 setOpenedToolTip
					 }) => {
	// If is empty value, return column with "-"
	if (!columnData) {
		return (
		  <td key={columnKey} colSpan={colSpan}>
			  –
		  </td>
		);
	}

	const value = <ValueContent value={columnData.char_val_value}/>;

	// If is tooltip characteristic
	if (columnData.char_val_value_is_tooltip) {
		return (
		  <td key={columnKey} colSpan={colSpan}>
			  <Tooltip
				id={columnData.char_val_id}
				title={language.clickForMoreInfo}
				tooltipText={value}
				openedId={openedToolTip}
				setOpenedId={setOpenedToolTip}
			  />
		  </td>
		);
	}

	// Return
	return (
	  <td key={columnKey} colSpan={colSpan}>
		  {value}
	  </td>
	);
};

// Value span content
const ValueContent = ({value}) => {
	return (
	  <span className={styles.valueSpan}>
			{renderTextWithPrices(value || '')}
	  </span>
	);
};

// Table with renew prices after characteristics table
const RenewTable = ({
						data,
						availablePlans,
						renewTerms,
						characteristicsServiceType
					}) => {
	return (
	  <div className={styles.tableWrap}>
		  <table
			className={`${styles.characteristicsTable} ${styles[characteristicsServiceType] ? styles[characteristicsServiceType] : ''}`}>
			  <TableHeader
				title={language.renewalPrices}
				availablePlans={availablePlans}
				data={data}
			  />

			  <tbody>
			  {renewTerms.map((renewTerm) => (
				<tr key={renewTerm.term}>
					<th>
						<span>
							{renewTerm.term} {renewTerm.term === 1 ? language.month : language.months}
						</span>
					</th>

					{availablePlans.map((option) => (
					  <td key={`${option.value}-${renewTerm.term}`}>
						  <Price
							serviceId={option.value}
							layout="PriceH"
							term={renewTerm.term}
							type="renew"
							emptyReturn="-"
						  />
					  </td>
					))}
				</tr>
			  ))}
			  </tbody>
		  </table>
	  </div>
	);
};

// Helper functions
////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////

const renderTextWithPrices = (text = '') => {
	const regex = /\{\{PRICE:([^}]+)\}\}/g;
	const parts = [];
	let lastIndex = 0;
	let match;

	while ((match = regex.exec(text)) !== null) {
		if (match.index > lastIndex) {
			parts.push(parse(text.slice(lastIndex, match.index)));
		}

		const params = {};

		match[1].split(';').forEach((pair) => {
			const [key, value] = pair.split('=');
			params[key] = value;
		});

		parts.push(
		  <Price
			key={`${params?.serviceId || 'price'}-${match.index}`}
			serviceId={params?.serviceId}
			term={params?.term}
			layout="PriceG"
		  />
		);

		lastIndex = regex.lastIndex;
	}

	if (lastIndex < text.length) {
		parts.push(parse(text.slice(lastIndex)));
	}

	return parts;
};

const isMutualCharacteristic = (characteristicsData, groupId, param, availablePlans = []) => {
	if (param?.hasMutual === true) {
		return true;
	}

	const planData = availablePlans.map((option) =>
	  getCharacteristicForPlan(characteristicsData, groupId, option.value, param.char_id)
	);

	const existingCharacteristics = planData.filter(Boolean);

	if (existingCharacteristics.length === 1 && existingCharacteristics[0]?.char_val_value) {
		return true;
	}

	if (existingCharacteristics.length === availablePlans.length) {
		const uniqueValues = new Set(
		  existingCharacteristics.map((item) => `${item.char_val_value}|||${item.char_val_value_is_tooltip}`)
		);

		if (uniqueValues.size === 1) {
			return true;
		}
	}

	const valueText = String(param?.char_val_value || '').toLowerCase();

	if (
	  valueText.includes('за всички планове') ||
	  valueText.includes('всички планове') ||
	  valueText.includes('included in all plans') ||
	  valueText.includes('unlimited for all plans')
	) {
		return true;
	}

	return false;
};

const getPlanName = (data, option) => {
	return data?.charTitles?.[option.value] || option.label;
};

const getGroupData = (characteristicsData, groupId) => {
	return characteristicsData?.[groupId] || {};
};

const getFirstAvailablePlanForGroup = (characteristicsData, groupId, availablePlans = []) => {
	return availablePlans.find((option) => getGroupData(characteristicsData, groupId)?.[option.value]);
};

const getGroupRows = (characteristicsData, groupId, availablePlans = []) => {
	const firstAvailablePlan = getFirstAvailablePlanForGroup(characteristicsData, groupId, availablePlans);

	if (!firstAvailablePlan) {
		return [];
	}

	const rawData = getGroupData(characteristicsData, groupId)?.[firstAvailablePlan.value];

	if (Array.isArray(rawData)) {
		return rawData;
	}

	if (rawData && typeof rawData === 'object') {
		return Object.values(rawData).flat();
	}

	return [];
};

const getCharacteristicForPlan = (characteristicsData, groupId, planId, charId) => {
	const planData = getGroupData(characteristicsData, groupId)?.[planId];

	if (!planData) {
		return null;
	}

	if (Array.isArray(planData)) {
		return planData.find((item) => item.char_id === charId) || null;
	}

	if (typeof planData === 'object') {
		return planData?.[charId]?.[0] || null;
	}

	return null;
};

export default CharacteristicsA;